import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import { storableError } from '../util/errors';
import { LISTING_STATE_DRAFT } from '../util/types';
import * as log from '../util/log';
import { getTransitionsNeedingProviderAttention } from '../transactions/transaction';
import { states as inquiryStates } from '../transactions/transactionProcessInquiry';
import { getMyAccountManager, getInboxCounts } from '../util/api';
import { newInboxEnabled } from '../util/newInbox';
import { authInfo } from './auth.duck';
import { fetchFavoriteListings } from './favoriteListings.duck';
import { stripeAccountCreateSuccess } from './stripeConnectAccount.duck';

// ================ Action types ================ //

export const CURRENT_USER_SHOW_REQUEST = 'app/user/CURRENT_USER_SHOW_REQUEST';
export const CURRENT_USER_SHOW_SUCCESS = 'app/user/CURRENT_USER_SHOW_SUCCESS';
export const CURRENT_USER_SHOW_ERROR = 'app/user/CURRENT_USER_SHOW_ERROR';

export const CLEAR_CURRENT_USER = 'app/user/CLEAR_CURRENT_USER';

export const FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST';
export const FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_LISTINGS_ERROR =
  'app/user/FETCH_CURRENT_USER_HAS_LISTINGS_ERROR';

export const FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST';
export const FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS';
export const FETCH_CURRENT_USER_NOTIFICATIONS_ERROR =
  'app/user/FETCH_CURRENT_USER_NOTIFICATIONS_ERROR';

export const FETCH_CURRENT_USER_HAS_ORDERS_REQUEST =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_REQUEST';
export const FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS =
  'app/user/FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS';
export const FETCH_CURRENT_USER_HAS_ORDERS_ERROR = 'app/user/FETCH_CURRENT_USER_HAS_ORDERS_ERROR';

export const FETCH_CURRENT_USER_ACCOUNT_MANAGER_SUCCESS =
  'app/user/FETCH_CURRENT_USER_ACCOUNT_MANAGER_SUCCESS';

export const SEND_VERIFICATION_EMAIL_REQUEST = 'app/user/SEND_VERIFICATION_EMAIL_REQUEST';
export const SEND_VERIFICATION_EMAIL_SUCCESS = 'app/user/SEND_VERIFICATION_EMAIL_SUCCESS';
export const SEND_VERIFICATION_EMAIL_ERROR = 'app/user/SEND_VERIFICATION_EMAIL_ERROR';

export const SET_UNREAD_MESSAGES_LOG = 'app/user/SET_UNREAD_MESSAGES_LOG';
export const SET_MEMBER_AREA_STATE = 'app/user/SET_MEMBER_AREA_STATE';

// ================ Reducer ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
      ? newCurrentUser
      : { id, type, attributes, ...oldRelationships, ...relationships };
};

const initialState = {
  currentUser: null,
  currentUserShowError: null,
  currentUserHasListings: false,
  currentUserHasListingsError: null,
  currentUserNotificationCount: 0,
  currentUserNotificationCountError: null,
  currentUserHasOrders: null, // This is not fetched unless unverified emails exist
  currentUserHasOrdersError: null,
  // The member's account manager, fetched after the current user loads. It used
  // to be read straight off `metadata.accountManager`, but the assignment now
  // lives in its own collection, so it takes one extra call.
  //
  // null means either "not fetched yet" or "this member has nobody assigned".
  // Nothing needs to tell those apart: the only thing that reads it is the
  // "Need help?" card, which is left out in both cases.
  currentUserAccountManager: null,
  sendVerificationEmailInProgress: false,
  sendVerificationEmailError: null,
  // The member area (REACT_APP_MEMBER_AREA): the Inbox badge number, the
  // bell's number of new notifications, and whether the "KYC approved" banner
  // was already shown. They come with the inbox counts (one request).
  memberArea: {
    // True once the counts have arrived (the KYC banner waits for it).
    loaded: false,
    inboxCount: 0,
    notificationsNew: 0,
    kycApprovedBannerShownAt: null,
  },
  unreadMessagesLog: {
    hasUnreadInMyListings: false,
    hasUnreadInMyResponses: false,
    hasUnreadInIntroduced: false,
  },
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case CURRENT_USER_SHOW_REQUEST:
      return { ...state, currentUserShowError: null };
    case CURRENT_USER_SHOW_SUCCESS:
      return { ...state, currentUser: mergeCurrentUser(state.currentUser, payload) };
    case CURRENT_USER_SHOW_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, currentUserShowError: payload };

    case CLEAR_CURRENT_USER:
      return {
        ...state,
        currentUser: null,
        currentUserShowError: null,
        currentUserHasListings: false,
        currentUserHasListingsError: null,
        currentUserNotificationCount: 0,
        currentUserNotificationCountError: null,
        // Cleared with the user. Leaving it would show the previous member's
        // manager to whoever signs in next on the same browser.
        currentUserAccountManager: null,
      };

    case FETCH_CURRENT_USER_ACCOUNT_MANAGER_SUCCESS:
      return { ...state, currentUserAccountManager: payload };

    case FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST:
      return { ...state, currentUserHasListingsError: null };
    case FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS:
      return { ...state, currentUserHasListings: payload.hasListings };
    case FETCH_CURRENT_USER_HAS_LISTINGS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasListingsError: payload };

    case FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST:
      return { ...state, currentUserNotificationCountError: null };
    case FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS:
      return { ...state, currentUserNotificationCount: payload.transactions.length };
    case FETCH_CURRENT_USER_NOTIFICATIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserNotificationCountError: payload };

    case FETCH_CURRENT_USER_HAS_ORDERS_REQUEST:
      return { ...state, currentUserHasOrdersError: null };
    case FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS:
      return { ...state, currentUserHasOrders: payload.hasOrders };
    case FETCH_CURRENT_USER_HAS_ORDERS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, currentUserHasOrdersError: payload };

    case SEND_VERIFICATION_EMAIL_REQUEST:
      return {
        ...state,
        sendVerificationEmailInProgress: true,
        sendVerificationEmailError: null,
      };
    case SEND_VERIFICATION_EMAIL_SUCCESS:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
      };
    case SEND_VERIFICATION_EMAIL_ERROR:
      return {
        ...state,
        sendVerificationEmailInProgress: false,
        sendVerificationEmailError: payload,
      };

    case SET_UNREAD_MESSAGES_LOG:
      return {
        ...state,
        unreadMessagesLog: payload,
      };

    case SET_MEMBER_AREA_STATE:
      return {
        ...state,
        memberArea: { ...state.memberArea, ...payload },
      };

    default: SET_UNREAD_MESSAGES_LOG
      return state;
  }
}

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};

export const verificationSendingInProgress = state => {
  return state.user.sendVerificationEmailInProgress;
};

// ================ Action creators ================ //

export const currentUserShowRequest = () => ({ type: CURRENT_USER_SHOW_REQUEST });

export const currentUserShowSuccess = user => ({
  type: CURRENT_USER_SHOW_SUCCESS,
  payload: user,
});

export const currentUserShowError = e => ({
  type: CURRENT_USER_SHOW_ERROR,
  payload: e,
  error: true,
});

export const clearCurrentUser = () => ({ type: CLEAR_CURRENT_USER });

const fetchCurrentUserHasListingsRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_REQUEST,
});

export const fetchCurrentUserHasListingsSuccess = hasListings => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_SUCCESS,
  payload: { hasListings },
});

const fetchCurrentUserHasListingsError = e => ({
  type: FETCH_CURRENT_USER_HAS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserNotificationsRequest = () => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_REQUEST,
});

export const fetchCurrentUserNotificationsSuccess = transactions => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_SUCCESS,
  payload: { transactions },
});

const fetchCurrentUserNotificationsError = e => ({
  type: FETCH_CURRENT_USER_NOTIFICATIONS_ERROR,
  error: true,
  payload: e,
});

const fetchCurrentUserHasOrdersRequest = () => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_REQUEST,
});

export const fetchCurrentUserHasOrdersSuccess = hasOrders => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_SUCCESS,
  payload: { hasOrders },
});

const fetchCurrentUserHasOrdersError = e => ({
  type: FETCH_CURRENT_USER_HAS_ORDERS_ERROR,
  error: true,
  payload: e,
});

export const sendVerificationEmailRequest = () => ({
  type: SEND_VERIFICATION_EMAIL_REQUEST,
});

export const sendVerificationEmailSuccess = () => ({
  type: SEND_VERIFICATION_EMAIL_SUCCESS,
});

export const sendVerificationEmailError = e => ({
  type: SEND_VERIFICATION_EMAIL_ERROR,
  error: true,
  payload: e,
});

export const setUnreadMessagesLog = log => ({
  type: SET_UNREAD_MESSAGES_LOG,
  payload: log,
});

export const setMemberAreaState = patch => ({
  type: SET_MEMBER_AREA_STATE,
  payload: patch,
});

// ================ Thunks ================ //

export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasListingsRequest());
  const { currentUser } = getState().user;

  if (!currentUser) {
    dispatch(fetchCurrentUserHasListingsSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    authorId: currentUser.id.uuid,
    page: 1,
    perPage: 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      dispatch(fetchCurrentUserHasListingsSuccess(!!hasPublishedListings));
    })
    .catch(e => dispatch(fetchCurrentUserHasListingsError(storableError(e))));
};

export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  dispatch(fetchCurrentUserHasOrdersRequest());

  if (!getState().user.currentUser) {
    dispatch(fetchCurrentUserHasOrdersSuccess(false));
    return Promise.resolve(null);
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      dispatch(fetchCurrentUserHasOrdersSuccess(!!hasOrders));
    })
    .catch(e => dispatch(fetchCurrentUserHasOrdersError(storableError(e))));
};

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;

export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  const transitionsNeedingAttention = getTransitionsNeedingProviderAttention();
  if (transitionsNeedingAttention.length === 0) {
    // Don't update state, if there's no need to draw user's attention after last transitions.
    return;
  }

  const apiQueryParams = {
    only: 'sale',
    last_transitions: transitionsNeedingAttention,
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
  };

  dispatch(fetchCurrentUserNotificationsRequest());
  sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      const transactions = response.data.data;
      dispatch(fetchCurrentUserNotificationsSuccess(transactions));
    })
    .catch(e => dispatch(fetchCurrentUserNotificationsError(storableError(e))));
};

export const fetchUnreadMessagesLog = () => async (dispatch, getState, sdk) => {
  // The new inbox keeps its own counts: one request to our server instead of
  // four Sharetribe queries.
  if (newInboxEnabled()) {
    try {
      const counts = await getInboxCounts();
      dispatch(
        setMemberAreaState({
          loaded: true,
          inboxCount: (counts?.tabs?.listings || 0) + (counts?.tabs?.enquiries || 0),
          // Only there while the member area is on.
          ...(counts?.memberArea || {}),
        })
      );
      return dispatch(
        setUnreadMessagesLog({
          hasUnreadInMyListings: counts?.tabs?.listings > 0,
          hasUnreadInMyResponses: counts?.tabs?.enquiries > 0,
          hasUnreadInIntroduced: false,
        })
      );
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  try {
    const nonIntroducedStates = Object.values(inquiryStates).filter(i => i !== "introduced");
    const nonIntroducedStatesString = nonIntroducedStates.map(i => `state/${i}`).join(",");

    const unreadInMyListing = sdk.transactions.query({
      only: 'sale',
      states: nonIntroducedStatesString,
      meta_providerHasUnreadMessages: true,
      'fields.transaction': ['metadata'],
      perPage: 1,
    });
    const unreadInMyResponses = sdk.transactions.query({
      only: 'order',
      states: nonIntroducedStatesString,
      meta_customerHasUnreadMessages: true,
      'fields.transaction': ['metadata'],
      perPage: 1,
    });
    const unreadInIntroducedCustomer = sdk.transactions.query({
      only: 'order',
      states: "state/introduced",
      meta_customerHasUnreadMessages: true,
      'fields.transaction': ['metadata'],
      perPage: 1,
    });
    const unreadInIntroducedProvider = sdk.transactions.query({
      only: 'sale',
      states: "state/introduced",
      meta_providerHasUnreadMessages: true,
      'fields.transaction': ['metadata'],
      perPage: 1,
    });

    const responses = await Promise.all([
      unreadInMyListing, unreadInMyResponses,
      unreadInIntroducedCustomer, unreadInIntroducedProvider,
    ]);

    const hasUnreadInMyListings = responses[0]?.data?.meta?.totalItems > 0;
    const hasUnreadInMyResponses = responses[1]?.data?.meta?.totalItems > 0;
    const hasUnreadInIntroducedCustomer = responses[2]?.data?.meta?.totalItems > 0;
    const hasUnreadInIntroducedProvider = responses[3]?.data?.meta?.totalItems > 0;
    const hasUnreadInIntroduced = hasUnreadInIntroducedCustomer || hasUnreadInIntroducedProvider;

    return dispatch(setUnreadMessagesLog({
      hasUnreadInMyListings,
      hasUnreadInMyResponses,
      hasUnreadInIntroduced,
    }));
  } catch (error) {
    console.error(error);
  };
};

export const fetchCurrentUserAccountManagerSuccess = manager => ({
  type: FETCH_CURRENT_USER_ACCOUNT_MANAGER_SUCCESS,
  payload: manager,
});

/**
 * Fetches the member's account manager.
 *
 * This is the extra call that came with moving the assignment out of
 * `metadata.accountManager` and into its own collection. It is deliberately
 * fire-and-forget: a failure here must never make signing in look broken, so
 * there is no error in the store and nothing waits on it. The only thing that
 * reads the result is the "Need help?" card on the Subscriptions page, which is
 * left out when there is nothing to show.
 */
export const fetchCurrentUserAccountManager = () => dispatch => {
  return getMyAccountManager()
    .then(response => {
      dispatch(fetchCurrentUserAccountManagerSuccess(response.manager || null));
    })
    .catch(e => {
      // Logged, not stored. Nothing in the app changes because of it.
      log.error(e, 'fetch-current-user-account-manager-failed');
    });
};

export const fetchCurrentUser = (params = null) => (dispatch, getState, sdk) => {
  dispatch(currentUserShowRequest());
  const { isAuthenticated } = getState().auth;

  if (!isAuthenticated) {
    // Make sure current user is null
    dispatch(currentUserShowSuccess(null));
    return Promise.resolve({});
  }

  // No profile image: avatars are always a placeholder on Parnexus.
  const parameters = params || {
    include: ['stripeAccount'],
  };

  return sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(stripeAccountCreateSuccess(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .then(currentUser => {
      dispatch(fetchCurrentUserHasListings());
      // Both read Sharetribe transactions, which the new inbox no longer uses.
      if (!newInboxEnabled()) {
        dispatch(fetchCurrentUserNotifications());
      }
      dispatch(fetchCurrentUserAccountManager());
      // Also finishes a save the member asked for before signing in.
      dispatch(fetchFavoriteListings());
      if (!currentUser.attributes.emailVerified && !newInboxEnabled()) {
        dispatch(fetchCurrentUserHasOrders());
      }
      const userLastOnline = currentUser.attributes.profile.publicData?.lastOnline;

      const currentTime = new Date();
      const threeMinutesAgo = new Date(currentTime - 3 * 60 * 1000);

      if (!userLastOnline || (userLastOnline && new Date(userLastOnline) < threeMinutesAgo)) {
        sdk.currentUser.updateProfile({
          publicData: { lastOnline: new Date().toISOString() },
        });
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
    })
    .then(() => {
      dispatch(fetchUnreadMessagesLog());
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      dispatch(currentUserShowError(storableError(e)));
    });
};

// Save the accepted Terms of Service version to the current user's
// protected data. We use a dedicated thunk (instead of ProfileSettingsPage's
// updateProfile) so we don't trigger that thunk's success popup or its
// "upload passport" redirect. On success the updated user is stored so the
// re-acceptance modal closes automatically.
export const acceptUpdatedTerms = termsVersion => (dispatch, getState, sdk) => {
  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return sdk.currentUser
    .updateProfile({ protectedData: { acceptedTermsVersion: termsVersion } }, queryParams)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
      }
      const currentUser = entities[0];
      dispatch(currentUserShowSuccess(currentUser));
      return currentUser;
    })
    .catch(e => {
      log.error(e, 'accept-updated-terms-failed');
      throw storableError(e);
    });
};

export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  if (verificationSendingInProgress(getState())) {
    return Promise.reject(new Error('Verification email sending already in progress'));
  }
  dispatch(sendVerificationEmailRequest());
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => dispatch(sendVerificationEmailSuccess()))
    .catch(e => dispatch(sendVerificationEmailError(storableError(e))));
};
